import React, { useEffect, useState } from 'react'
import { IconArrowRight, IconPhone } from '@tabler/icons-react'
import PremiumButton from './shared/PremiumButton'
import GradientText from './shared/GradientText'
import '../styles/designTokens.css'

export default function HeroSection() {
  const [isVisible, setIsVisible] = useState(false)

  useEffect(() => {
    setIsVisible(true)
  }, [])

  const handleLearnMore = () => {
    const scenariosSection = document.getElementById('scenarios')
    scenariosSection?.scrollIntoView({ behavior: 'smooth' })
  }

  const handleConsult = () => {
    window.location.href = 'tel:400-XXX-XXXX'
  }

  const badges = [
    '专为中小企业和一线业务团队设计',
    '多源合规数据 · 一份清晰风险报告',
    '支持租赁、家政、人力、企业合作等多种场景',
  ]

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-[#FFF7ED] via-white to-[#F0FDFA]">
      <div className="absolute inset-0 gradient-mesh"></div>

      <div className="absolute inset-0 noise-texture"></div>

      <div className="absolute top-20 right-[10%] w-[500px] h-[500px] bg-gradient-to-br from-orange-200/30 to-orange-100/20 rounded-full blur-3xl animate-[floatSlow_8s_ease-in-out_infinite]"></div>
      <div className="absolute bottom-20 left-[5%] w-[400px] h-[400px] bg-gradient-to-br from-blue-100/20 to-teal-100/20 rounded-full blur-3xl animate-[floatSlow_10s_ease-in-out_infinite_reverse]"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 py-32">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <div
            className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            <div className="flex flex-wrap gap-3">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-4 py-2 rounded-full text-xs font-medium bg-white/80 backdrop-blur-sm text-gray-700 border border-orange-100 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5"
                  style={{
                    animationDelay: `${index * 100}ms`,
                    animation: 'fadeInUp 0.6s ease-out forwards',
                  }}
                >
                  {badge}
                </span>
              ))}
            </div>

            <div className="space-y-6">
              <h1 className="space-y-2">
                <span className="block text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight text-[#0C0A09]">
                  优查查
                </span>
                <span className="block text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-semibold text-gray-600 whitespace-nowrap">
                  中小企业也用得起的
                </span>
                <GradientText className="block text-4xl sm:text-5xl lg:text-6xl xl:text-7xl whitespace-nowrap">
                  智能风控助手
                </GradientText>
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 leading-relaxed max-w-2xl font-light">
                不用懂复杂风控模型，只需几个关键信息，优查查就能帮你快速看清「这个人/这家公司是否可靠」，让租赁、用工、合作前的每一次选择更稳妥。
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <PremiumButton
                variant="primary"
                size="lg"
                onClick={handleLearnMore}
                icon={<IconArrowRight size={20} />}
                iconPosition="right"
              >
                马上了解如何使用
              </PremiumButton>
              <PremiumButton
                variant="secondary"
                size="lg"
                onClick={handleConsult}
                icon={<IconPhone size={20} />}
                iconPosition="left"
              >
                预约顾问一对一讲解
              </PremiumButton>
            </div>

            <p className="text-xs text-gray-400 leading-relaxed max-w-2xl pt-4">
              优查查仅在合法授权前提下，为机构客户提供风险评估与背景核查辅助服务，不提供任何形式的放贷、下款、催收及相关金融业务。
            </p>
          </div>

          <div
            className={`hidden lg:block relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-10'}`}
          >
            <div className="relative w-full h-[600px]">
              <div className="absolute top-0 right-0 w-80 h-80 bg-gradient-to-br from-orange-200 to-orange-100 rounded-[60px] rotate-6 shadow-2xl shadow-orange-200/50 animate-[floatFast_6s_ease-in-out_infinite]"></div>
              <div className="absolute bottom-20 right-20 w-64 h-64 bg-gradient-to-br from-blue-100 to-teal-100 rounded-[50px] -rotate-12 shadow-xl shadow-blue-200/30 animate-[floatFast_7s_ease-in-out_infinite_reverse]"></div>
              <div className="absolute top-1/2 left-0 w-48 h-48 bg-gradient-to-br from-orange-50 to-white rounded-[40px] rotate-3 shadow-lg animate-[floatFast_5s_ease-in-out_infinite]"></div>

              <div className="absolute top-1/4 right-1/4 w-16 h-16 bg-white rounded-2xl shadow-xl flex items-center justify-center animate-[floatSlow_4s_ease-in-out_infinite]">
                <div className="w-8 h-8 bg-gradient-to-br from-orange-400 to-orange-600 rounded-lg"></div>
              </div>
              <div className="absolute bottom-1/3 left-1/3 w-12 h-12 bg-white rounded-xl shadow-lg flex items-center justify-center animate-[floatSlow_5s_ease-in-out_infinite_reverse]">
                <div className="w-6 h-6 bg-gradient-to-br from-blue-400 to-teal-500 rounded-md"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent"></div>
    </section>
  )
}
